import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/shared/api/http-client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import type { PendingVenue } from "./types";
import { useRequestChanges } from "./use-request-changes";

const MIN_MESSAGE_LENGTH = 10;
const MAX_MESSAGE_LENGTH = 1000;

interface RequestChangesDialogProps {
  readonly venue: PendingVenue | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess: () => void;
}

export function RequestChangesDialog({
  venue,
  open,
  onOpenChange,
  onSuccess,
}: RequestChangesDialogProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const requestChanges = useRequestChanges();

  // Reset form state when venue changes or dialog reopens
  useEffect(() => {
    if (open) {
      setMessage("");
      setError(null);
    }
  }, [venue?.id, open]);

  if (!venue) return null;

  const trimmed = message.trim();
  const isValid = trimmed.length >= MIN_MESSAGE_LENGTH && trimmed.length <= MAX_MESSAGE_LENGTH;

  const handleSubmit = () => {
    if (!isValid) return;
    setError(null);
    requestChanges.mutate(
      { venueId: venue.id, input: { message: trimmed } },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess();
        },
        onError: (err) => {
          if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
            // TODO(API-0.3): same MFA branch as ApproveVenueDialog
            setError(t("venues.errors.forbidden"));
          } else {
            setError(t("venues.requestChanges.error"));
          }
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="request-changes-dialog">
        <DialogHeader>
          <DialogTitle>{t("venues.requestChanges.title")}</DialogTitle>
          <DialogDescription>
            {t("venues.requestChanges.description", { name: venue.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4">
          <label
            htmlFor="request-changes-message"
            className="font-sans text-sm font-medium text-foreground"
          >
            {t("venues.requestChanges.messageLabel")}
          </label>
          <Textarea
            id="request-changes-message"
            data-testid="request-changes-textarea"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("venues.requestChanges.messagePlaceholder")}
            rows={4}
            maxLength={MAX_MESSAGE_LENGTH}
          />
          <div className="flex justify-between">
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {t("venues.requestChanges.messageHint", { min: MIN_MESSAGE_LENGTH })}
            </p>
            <p className="font-sans text-xs font-normal text-muted-foreground">
              {trimmed.length}/{MAX_MESSAGE_LENGTH}
            </p>
          </div>

          {error && (
            <p
              data-testid="request-changes-error"
              className="font-sans text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={requestChanges.isPending}
          >
            {t("venues.actions.cancel")}
          </Button>
          <Button
            data-testid="confirm-request-changes-button"
            variant="secondary"
            onClick={handleSubmit}
            disabled={!isValid || requestChanges.isPending}
          >
            {requestChanges.isPending
              ? t("venues.requestChanges.sending")
              : t("venues.actions.requestChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
